import { comboboxAnatomy } from "@chakra-ui/react-v3/anatomy";
import { defineSlotRecipe } from "@chakra-ui/react-v3";

export const comboboxRecipe = defineSlotRecipe({
  slots: comboboxAnatomy.keys(),

  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: "1.5",
      width: "full",
    },

    label: {
      fontWeight: "medium",
      userSelect: "none",
      _disabled: {
        opacity: "0.5",
        cursor: "not-allowed",
      },
    },

    control: {
      pos: "relative",
      "--padding-factor": "1",
      "--combobox-input-padding-end": "var(--combobox-input-padding-x)",
      "&:has([data-part=trigger]), &:has([data-part=clear-trigger])": {
        "--combobox-input-padding-end":
          "calc(var(--combobox-input-height) * var(--padding-factor))",
      },
      "&:has([data-part=trigger]):has([data-part=clear-trigger]:not([hidden]))":
        {
          "--padding-factor": "1.5",
        },
    },

    input: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "full",
      minH: "var(--combobox-input-height)",
      ps: "var(--combobox-input-padding-x)",
      pe: "var(--combobox-input-padding-end)",
      "--input-height": "var(--combobox-input-height)",
      "--focus-color": "colors.colorPalette.focusRing",
      outline: 0,
      userSelect: "none",
      textAlign: "start",
      _placeholderShown: {
        color: "fg.muted",
      },
      _disabled: {
        opacity: "0.5",
        cursor: "not-allowed",
      },
    },

    trigger: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "--input-height": "var(--combobox-input-height)",
    },

    clearTrigger: {
      color: "fg.muted",
      pointerEvents: "auto",
      focusVisibleRing: "inside",
      focusRingWidth: "2px",
    },

    indicatorGroup: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "1",
      pos: "absolute",
      insetEnd: "0",
      top: "0",
      bottom: "0",
      px: "var(--combobox-input-padding-x)",
      _icon: {
        boxSize: "var(--combobox-indicator-size)",
      },
      "[data-disabled] &": {
        opacity: 0.5,
      },
    },

    content: {
      display: "flex",
      flexDirection: "column",
      outline: 0,
      maxH: "96",
      overflowY: "auto",
      transformOrigin: "var(--transform-origin)",
      _open: {
        "&[data-placement^=top]": {
          animationName: "slide-from-bottom, fade-in",
        },
        "&[data-placement^=bottom]": {
          animationName: "slide-from-top, fade-in",
        },
        "&[data-placement^=left]": {
          animationName: "slide-from-right, fade-in",
        },
        "&[data-placement^=right]": {
          animationName: "slide-from-left, fade-in",
        },
        animationDuration: "fast",
      },
      _closed: {
        "&[data-placement^=top]": {
          animationName: "slide-to-bottom, fade-out",
        },
        "&[data-placement^=bottom]": {
          animationName: "slide-to-top, fade-out",
        },
        "&[data-placement^=left]": {
          animationName: "slide-to-right, fade-out",
        },
        "&[data-placement^=right]": {
          animationName: "slide-to-left, fade-out",
        },
        animationDuration: "0s",
      },
      "&[data-empty]:not(:has([data-scope=combobox][data-part=empty]))": {
        opacity: 0,
      },
    },

    item: {
      position: "relative",
      userSelect: "none",
      display: "flex",
      alignItems: "center",
      gap: "2",
      py: "var(--combobox-item-padding-y)",
      px: "var(--combobox-item-padding-x)",
      justifyContent: "space-between",
      flex: "1",
      textAlign: "start",
      _highlighted: {
        bg: "bg.emphasized/60",
      },
      _disabled: {
        pointerEvents: "none",
        opacity: "0.5",
      },
      _icon: {
        boxSize: "var(--combobox-indicator-size)",
      },
    },

    empty: {
      py: "var(--combobox-item-padding-y)",
      px: "var(--combobox-item-padding-x)",
    },

    itemText: {
      flex: "1",
    },

    itemGroup: {
      pb: "var(--combobox-item-padding-y)",
      _last: {
        pb: "0",
      },
    },

    itemGroupLabel: {
      fontWeight: "medium",
      py: "var(--combobox-item-padding-y)",
      px: "var(--combobox-item-padding-x)",
    },
  },

  variants: {
    variant: {
      outline: {
        input: {
          bg: "transparent",
          borderWidth: "1px",
          focusVisibleRing: "inside",
        },
      },
      subtle: {
        input: {
          borderWidth: "1px",
          borderColor: "transparent",
          focusVisibleRing: "inside",
        },
      },
      flushed: {
        input: {
          bg: "transparent",
          borderBottomWidth: "1px",
          borderRadius: "0",
          px: "0",
          _focusVisible: {
            borderColor: "var(--focus-color)",
            boxShadow: "0px 1px 0px 0px var(--focus-color)",
          },
        },
        indicatorGroup: {
          px: "0",
        },
      },
    },

    size: {
      xs: {
        root: {
          "--combobox-input-height": "sizes.8",
          "--combobox-input-padding-x": "spacing.2",
          "--combobox-indicator-size": "sizes.3.5",
        },
        content: {
          "--combobox-item-padding-x": "spacing.1.5",
          "--combobox-item-padding-y": "spacing.1",
          "--combobox-indicator-size": "sizes.3.5",
          p: "1",
        },
        trigger: {
          gap: "1",
        },
      },
      sm: {
        root: {
          "--combobox-input-height": "sizes.9",
          "--combobox-input-padding-x": "spacing.2.5",
          "--combobox-indicator-size": "sizes.4",
        },
        content: {
          "--combobox-item-padding-x": "spacing.2",
          "--combobox-item-padding-y": "spacing.1.5",
          "--combobox-indicator-size": "sizes.4",
          p: "1",
        },
        trigger: {
          gap: "1",
        },
      },
      md: {
        root: {
          "--combobox-input-height": "sizes.10",
          "--combobox-input-padding-x": "spacing.3",
          "--combobox-indicator-size": "sizes.4",
        },
        content: {
          "--combobox-item-padding-x": "spacing.2",
          "--combobox-item-padding-y": "spacing.1.5",
          "--combobox-indicator-size": "sizes.4",
          p: "1",
        },
        itemIndicator: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        trigger: {
          gap: "2",
        },
      },
      lg: {
        root: {
          "--combobox-input-height": "sizes.12",
          "--combobox-input-padding-x": "spacing.4",
          "--combobox-indicator-size": "sizes.5",
        },
        content: {
          "--combobox-item-padding-y": "spacing.2",
          "--combobox-item-padding-x": "spacing.3",
          "--combobox-indicator-size": "sizes.5",
          p: "1.5",
        },
        trigger: {
          py: "3",
          gap: "2",
        },
      },
    },
  },

  defaultVariants: {
    size: "md",
    variant: "outline",
  },
});
